import Phaser from "phaser";
import type { BattleReport, BattleResult, LaneBattleAnalysis } from "../../../shared/types/BattleEvent";
import type { Language } from "../i18n";
import { t } from "../i18n";
import { getOrCreatePlayerId, loadPlayerMemory } from "../ai/localMemory";
import { AudioManager } from "../game/AudioManager";
import { dailyChallengeFromSeed, dailyScore } from "../game/dailyChallenge";
import { rivalryStageLabel } from "./rivalry";
import { buildCareerShareText, buildDailyShareText, dailyDeepLink, shareOrCopy, type ShareOutcome } from "./shareResult";

type ResultPanelActions = {
  canReplay?: boolean;
  onReplay?: () => void;
  onReturn?: () => void;
};

export class ResultPanel {
  constructor(scene: Phaser.Scene, report: BattleReport | undefined, result: "win" | "loss", language: Language, battleResult?: BattleResult, actions: ResultPanelActions = {}) {
    const safeReport = normalizeReport(report, result, language);
    const laneAnalysis = rankedLaneAnalysis(battleResult);
    const learnedInsights = learnedInsightLines(safeReport, battleResult, language);
    scene.add.rectangle(640, 360, 820, 540, 0x14141c, 0.94).setStrokeStyle(2, 0xc9b878, 1);
    scene.add
      .text(640, 118, result === "win" ? t(language, "victory") : t(language, "castleBroken"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "42px",
        color: result === "win" ? "#9be8ff" : "#ff8c73",
        stroke: "#111016",
        strokeThickness: 6
      })
      .setOrigin(0.5);
    this.renderRewards(scene, language);
    this.renderDailyResult(scene, language, battleResult);
    scene.add
      .text(260, 170, safeReport.commanderComment, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "17px",
        color: "#f6f0df",
        wordWrap: { width: 760 },
        stroke: "#111016",
        strokeThickness: 3
      });

    scene.add.text(260, 250, t(language, "goodMoves"), sectionTitleStyle("#9be8ff"));
    scene.add.text(260, 276, bulletList(safeReport.playerGoodMoves), bodyStyle("#dff9ff", 340));
    scene.add.text(680, 250, t(language, "fixNext"), sectionTitleStyle("#ffcc8e"));
    scene.add.text(680, 276, bulletList(safeReport.playerMistakes), bodyStyle("#ffe0c2", 300));

    scene.add.text(260, 374, language === "ko" ? "전선 분석" : "Lane Analysis", sectionTitleStyle("#c9b878"));
    laneAnalysis.slice(0, 5).forEach((lane, index) => {
      const y = 402 + index * 24;
      scene.add.text(260, y, laneSummary(lane, language), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: laneVerdictColor(lane.verdict),
        wordWrap: { width: 760 },
        stroke: "#111016",
        strokeThickness: 3
      });
    });

    if (learnedInsights.length > 0) {
      scene.add.text(260, 512, `${language === "ko" ? "AI가 배운 것" : "AI Learned"}: ${learnedInsights.slice(0, 2).join(" / ")}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#d9c7ff",
        wordWrap: { width: 760 },
        stroke: "#111016",
        strokeThickness: 3
      });
    }

    if ((battleResult?.mercySummary?.grantedCount ?? 0) > 0) {
      scene.add.text(260, learnedInsights.length > 0 ? 534 : 512, mercySummaryLine(battleResult!, language), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#ffcc8e",
        wordWrap: { width: 760 },
        stroke: "#111016",
        strokeThickness: 3
      });
    }

    scene.add
      .text(260, learnedInsights.length > 0 || (battleResult?.mercySummary?.grantedCount ?? 0) > 0 ? 558 : 538, `${t(language, "nextThreat")}: ${safeReport.nextBattleThreat}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "15px",
        color: "#ffe58e",
        wordWrap: { width: 760 },
        stroke: "#111016",
        strokeThickness: 3
      });
    const shareLabel = t(language, "share");
    const doShare = () => void this.shareResult(scene, language, result, battleResult);
    const menuLabel = language === "ko" ? "메뉴로" : "Menu";
    if (actions.canReplay && actions.onReplay) {
      createActionButton(scene, 454, 610, language === "ko" ? "전투 기록 보기" : "View Replay", 0x17364a, actions.onReplay);
      createActionButton(scene, 640, 610, shareLabel, 0x3a2a12, doShare);
      createActionButton(scene, 826, 610, menuLabel, 0x2a211b, actions.onReturn);
    } else {
      createActionButton(scene, 548, 610, shareLabel, 0x3a2a12, doShare);
      createActionButton(scene, 732, 610, menuLabel, 0x17364a, actions.onReturn);
    }
  }

  /** Share the run (daily score or rivalry) via the Web Share API, copy-to-clipboard fallback. */
  private async shareResult(scene: Phaser.Scene, language: Language, result: "win" | "loss", battleResult?: BattleResult) {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    let text: string;
    if (battleResult?.mode === "daily" && typeof battleResult.dailySeed === "number") {
      const challenge = dailyChallengeFromSeed(battleResult.dailySeed);
      const score = dailyScore({ win: result === "win", battleTime: battleResult.battleTime, weight: challenge.weight });
      text = buildDailyShareText(language, { score, streak: memory.dailyChallenge?.streak ?? 0, won: result === "win" });
    } else {
      text = buildCareerShareText(language, {
        rivalryStageLabel: rivalryStageLabel(memory.rivalryStage, language),
        nickname: memory.nickname,
        level: memory.commanderLevel
      });
    }
    const outcome = await shareOrCopy({ title: "Duel Siege AI", text, url: dailyDeepLink() });
    this.shareToast(scene, language, outcome);
  }

  private shareToast(scene: Phaser.Scene, language: Language, outcome: ShareOutcome) {
    if (outcome === "failed") return;
    const label = scene.add
      .text(640, 576, outcome === "copied" ? t(language, "linkCopied") : t(language, "shared"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "16px",
        color: "#9ff0b0",
        stroke: "#111016",
        strokeThickness: 4
      })
      .setOrigin(0.5)
      .setDepth(60);
    scene.tweens.add({ targets: label, y: 552, alpha: 0, duration: 1500, delay: 500, onComplete: () => label.destroy() });
  }

  /** Show the XP/War Funds earned this battle (read from the just-saved PlayerMemory). */
  private renderRewards(scene: Phaser.Scene, language: Language) {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    const reward = memory.lastRewardSummary;
    if (!reward) return;
    if (reward.leveledUp) {
      scene.time.delayedCall(700, () => AudioManager.playSfx("levelUp"));
    }
    const levelTag = reward.leveledUp ? `  ·  ${t(language, "levelUp")} Lv ${reward.newLevel}` : "";
    const line = `${t(language, "rewardsTitle")}  +${reward.xp} ${t(language, "xpGained")}  ·  +${reward.funds} ${t(language, "warFunds")}${levelTag}`;
    scene.add
      .text(640, 152, line, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "15px",
        color: reward.leveledUp ? "#ffe58e" : "#a7d8ff",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setOrigin(0.5);

    const unlocked = reward.unlockedAchievements ?? [];
    if (unlocked.length > 0) {
      const names = unlocked.map((id) => t(language, `ach_${id}` as Parameters<typeof t>[1])).join(", ");
      scene.add
        .text(640, 200, `🏅 ${t(language, "achievementUnlocked")}: ${names}`, {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: "14px",
          color: "#ffe58e",
          stroke: "#111016",
          strokeThickness: 3,
          wordWrap: { width: 720 },
          align: "center"
        })
        .setOrigin(0.5);
    }
  }

  /** Daily Seeded Challenge feedback: this run's score, the day's best, and the streak. */
  private renderDailyResult(scene: Phaser.Scene, language: Language, battleResult?: BattleResult) {
    if (battleResult?.mode !== "daily" || typeof battleResult.dailySeed !== "number") return;
    const record = loadPlayerMemory(getOrCreatePlayerId()).dailyChallenge;
    const challenge = dailyChallengeFromSeed(battleResult.dailySeed);
    const score = dailyScore({ win: battleResult.result === "win", battleTime: battleResult.battleTime, weight: challenge.weight });
    const best = record?.bestScore ?? score;
    const streak = record?.streak ?? 0;
    const bestTag = score >= best ? `  ·  🏆 ${t(language, "dailyNewBest")}` : "";
    const line = `${t(language, "dailyChallenge")}  ${t(language, "dailyRunScore")} ${score}  ·  ${t(language, "dailyBestScore")} ${best}  ·  ${t(language, "dailyStreak")} ${streak}${bestTag}`;
    scene.add
      .text(640, 226, line, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#ffcf6b",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setOrigin(0.5);
  }
}

function createActionButton(scene: Phaser.Scene, x: number, y: number, label: string, color: number, onClick?: () => void) {
  const button = scene.add.container(x, y);
  const frame = scene.add.rectangle(0, 0, 176, 40, color, 0.92).setStrokeStyle(1, 0xc9b878, 0.9);
  const text = scene.add
    .text(0, 0, label, {
      fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
      fontSize: "15px",
      color: "#f7f2df",
      stroke: "#111016",
      strokeThickness: 3,
      fixedWidth: 156
    })
    .setOrigin(0.5)
    .setAlign("center");
  button.add([frame, text]);
  button.setSize(176, 40);
  button.setInteractive({ useHandCursor: Boolean(onClick) });
  button.on("pointerover", () => frame.setAlpha(1));
  button.on("pointerout", () => frame.setAlpha(0.92));
  button.on("pointerdown", () => onClick?.());
  return button;
}

function sectionTitleStyle(color: string): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: "15px",
    color,
    stroke: "#111016",
    strokeThickness: 3
  };
}

function bodyStyle(color: string, width: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: "14px",
    color,
    wordWrap: { width },
    stroke: "#111016",
    strokeThickness: 3
  };
}

function bulletList(items: string[]) {
  return items.slice(0, 2).map((item) => `- ${item}`).join("\n");
}

function rankedLaneAnalysis(battleResult: BattleResult | undefined): LaneBattleAnalysis[] {
  const laneAnalysis = battleResult?.laneAnalysis ?? [];
  if (laneAnalysis.length > 0) {
    return [...laneAnalysis].sort((a, b) => laneActivityScore(b) - laneActivityScore(a));
  }
  return ([1, 2, 3, 4, 5] as const).map((lane) => ({
    lane,
    playerDamageToAiCastle: 0,
    aiDamageToPlayerCastle: 0,
    playerUnitsSummoned: 0,
    enemyUnitsKilled: 0,
    playerUnitsLost: 0,
    laneBreaches: 0,
    moveCommands: 0,
    aiManeuvers: 0,
    verdict: "quiet"
  }));
}

function laneActivityScore(lane: LaneBattleAnalysis) {
  return lane.playerDamageToAiCastle + lane.aiDamageToPlayerCastle + lane.enemyUnitsKilled * 18 + lane.playerUnitsLost * 16 + lane.playerUnitsSummoned * 6 + lane.laneBreaches * 80 + lane.moveCommands * 8 + lane.aiManeuvers * 10;
}

function learnedInsightLines(report: BattleReport, battleResult: BattleResult | undefined, language: Language) {
  if (report.learnedInsights?.length) return report.learnedInsights.slice(0, 2);
  const lane = rankedLaneAnalysis(battleResult)[0];
  if (!lane) return [];
  if (language === "ko") {
    return [`전선 ${lane.lane} ${lane.verdict}`, `소환 ${lane.playerUnitsSummoned} / 손실 ${lane.playerUnitsLost}`];
  }
  return [`Lane ${lane.lane} ${lane.verdict}`, `Summons ${lane.playerUnitsSummoned} / losses ${lane.playerUnitsLost}`];
}

function laneSummary(lane: LaneBattleAnalysis, language: Language) {
  if (language === "ko") {
    return `전선 ${lane.lane} ${laneVerdictLabel(lane, language)}  적성 피해 ${lane.playerDamageToAiCastle} / 아군성 피해 ${lane.aiDamageToPlayerCastle}  처치 ${lane.enemyUnitsKilled} 손실 ${lane.playerUnitsLost}`;
  }
  return `Lane ${lane.lane} ${laneVerdictLabel(lane, language)}  Enemy castle ${lane.playerDamageToAiCastle} / Your castle ${lane.aiDamageToPlayerCastle}  Kills ${lane.enemyUnitsKilled} Losses ${lane.playerUnitsLost}`;
}

function laneVerdictLabel(lane: LaneBattleAnalysis, language: Language) {
  const ko: Record<LaneBattleAnalysis["verdict"], string> = {
    breakthrough: "돌파 성공",
    held: "방어 성공",
    collapsed: "붕괴",
    traded: "교전",
    quiet: "조용함"
  };
  const en: Record<LaneBattleAnalysis["verdict"], string> = {
    breakthrough: "Breakthrough",
    held: "Held",
    collapsed: "Collapsed",
    traded: "Traded",
    quiet: "Quiet"
  };
  return language === "ko" ? ko[lane.verdict] : en[lane.verdict];
}

function laneVerdictColor(verdict: LaneBattleAnalysis["verdict"]) {
  if (verdict === "breakthrough" || verdict === "held") return "#b7f7d0";
  if (verdict === "collapsed") return "#ff9d8a";
  if (verdict === "traded") return "#f7de9c";
  return "#b8c4d8";
}

function mercySummaryLine(battleResult: BattleResult, language: Language) {
  const summary = battleResult.mercySummary;
  if (!summary) return "";
  if (language === "ko") {
    return `굴욕/자비 기록: ${summary.grantedCount}회 발동 (간청 ${summary.pleaCount}, 자동 완화 ${summary.autoCount}, 약화된 적 ${summary.softenedEnemies})`;
  }
  return `Mercy record: ${summary.grantedCount} granted (pleas ${summary.pleaCount}, automatic ${summary.autoCount}, enemies softened ${summary.softenedEnemies})`;
}

function normalizeReport(report: BattleReport | undefined, result: "win" | "loss", language: Language): BattleReport {
  const fallback = fallbackReport(result, language);
  return {
    ...fallback,
    result,
    commanderComment: cleanText(report?.commanderComment, fallback.commanderComment, language, 210),
    playerMistakes: cleanList(report?.playerMistakes, fallback.playerMistakes, language, 90),
    playerGoodMoves: cleanList(report?.playerGoodMoves, fallback.playerGoodMoves, language, 90),
    nextBattleThreat: cleanText(report?.nextBattleThreat, fallback.nextBattleThreat, language, 120),
    memoryUpdateSummary: cleanText(report?.memoryUpdateSummary, fallback.memoryUpdateSummary, language, 180),
    learnedInsights: cleanList(report?.learnedInsights, fallback.learnedInsights ?? [], language, 90),
    strategyFaced: report?.strategyFaced ?? fallback.strategyFaced,
    eventFaced: report?.eventFaced ?? fallback.eventFaced
  };
}

function cleanList(items: string[] | undefined, fallback: string[], language: Language, maxLength: number) {
  const cleaned = (items ?? [])
    .map((item) => cleanText(item, "", language, maxLength))
    .filter(Boolean);
  return cleaned.length > 0 ? cleaned.slice(0, 3) : fallback;
}

function cleanText(value: string | undefined, fallback: string, language: Language, maxLength: number) {
  const text = String(value ?? "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[{}\[\]"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || looksWrongLanguage(text, language)) return fallback;
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
}

function looksWrongLanguage(text: string, language: Language) {
  const hangulCount = (text.match(/[가-힣]/g) ?? []).length;
  if (language === "ko") return hangulCount === 0;
  return hangulCount > text.length * 0.35;
}

function fallbackReport(result: "win" | "loss", language: Language): BattleReport {
  if (language === "ko") {
    return {
      result,
      commanderComment: result === "win" ? "네가 요새를 뚫었다. 다음에는 그 길을 먼저 막겠다." : "성벽이 무너졌다. 가장 늦게 반응한 전선이 답이었다.",
      playerMistakes: result === "win" ? ["정찰 공백이 잠깐 생겼습니다.", "결정타를 조금 더 빨리 넣을 수 있었습니다."] : ["위협 전선에 병력 집중이 늦었습니다.", "반격 자원을 충분히 빨리 전환하지 못했습니다."],
      playerGoodMoves: result === "win" ? ["주요 전선 압박을 뚫고 승리했습니다.", "성 방어와 역공 타이밍이 맞았습니다."] : ["초반 파도를 지연했습니다.", "일부 전선에서는 요새를 위협했습니다."],
      nextBattleThreat: "주력 전선 방어와 빈 전선 압박을 동시에 시도할 것입니다.",
      memoryUpdateSummary: "전선 집중, 반격 타이밍, 반복 소환 패턴을 기록했습니다.",
      learnedInsights: ["주력 전선과 빈 전선 대응을 기억했습니다."],
      strategyFaced: "split_push"
    };
  }
  return {
    result,
    commanderComment: result === "win" ? "You broke the fortress. Next time, that road closes first." : "The wall fell where your response arrived last.",
    playerMistakes: result === "win" ? ["A scouting gap opened briefly.", "The finishing push could have landed sooner."] : ["The main threat lane was reinforced too late.", "Counter resources were converted too slowly."],
    playerGoodMoves: result === "win" ? ["Broke through the main pressure lane.", "Matched defense and counterattack timing well."] : ["Delayed the early waves.", "Threatened the fortress on some lanes."],
    nextBattleThreat: "Expect main-lane defense paired with pressure on empty lanes.",
    memoryUpdateSummary: "Recorded lane commitment, counter timing, and repeated summon patterns.",
    learnedInsights: ["Remembered main-lane pressure and empty-lane exposure."],
    strategyFaced: "split_push"
  };
}
